/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import Async from 'react-select';
import 'react-select/dist/react-select.css';
import { deliveryBoxSize, serviceTypes, vanSizes } from '../../consts';


type ExtraSkillOption = {
  label: string;
  icon_name: string;
};

type ExtraSkillsData = {
  [key: string]: {
    label: string;
    icon_name?: string;
    true?: ExtraSkillOption;
    false?: ExtraSkillOption;
  };
}

type TSelectRoleComponent = {
    serviceKey: string;
    selectedRole: any;
    servicesStructure: any;
    disabled: boolean;
    fields: any;
    update: any;
    editingID?: number;
};

export default function ExtraSkillsSelect({
    serviceKey,
    selectedRole,
    servicesStructure,
    disabled,
    fields,
    update,
    editingID,
}: TSelectRoleComponent) {

    const [selectedSkills, setSelectedSkills] = useState([]);
    const [extraSkills, setExtraSkills] = useState([]);
    const [roleFields, setRoleFields] = useState(fields);

    const roleFilterName = servicesStructure[serviceKey]?.role_filter_name;
    const isMotorcycle = serviceKey === serviceTypes.DRIVER
        && selectedRole?.vehicle_type_name === 'Motorcycle / Scooter';
    const isVan = serviceKey === serviceTypes.DRIVER && selectedRole?.vehicle_type_name === 'Van';

    const setOptionsAndDefaultValues = (structure: any) => {
        if (structure) {
            const serviceKeyData = servicesStructure[serviceKey];
            const roleName = serviceKey === serviceTypes.DRIVER ? 'vehicle_type' : 'type';
            const extraSkillsData: ExtraSkillsData = serviceKeyData?.[roleFilterName][selectedRole[roleName]] || {};

            if (extraSkillsData) {
                const options = Object.entries(extraSkillsData)
                    // Filter out keys that are not skills or should not be displayed
                    .filter(([key]) => !['label', 'icon_name', 'own_vehicle', 'heavy_lifting'].includes(key))
                    .flatMap(([key, value]) => {
                        if (typeof value === 'object' && !(value.true && value.false)) {
                            // Map entries of nested object to options (ex. delivery_box)
                            return Object.entries(value).map(([subKey, subValue]) => ({
                                // @ts-ignore
                                label: subValue?.label,
                                value: subKey,
                            }));
                        }
                        if (value.true && value.true.label) {
                            // Process skills with a true/false pair
                            return [{
                                label: value.true.label,
                                value: key,
                            }];
                        }
                        return [];
                    });
                setExtraSkills(options);

                const defaultSelectedSkillsValues = Object.entries(selectedRole)
                    .filter(([key, value]) => value === true && key !== 'active' && key !== 'delivery_box')
                    .map(([key]) => key);

                // Handle the special case for Motorcycle/Scooter's delivery box
                if (isMotorcycle) {
                    defaultSelectedSkillsValues.push('delivery_box');
                }

                const defaultSelectedSkills = options.filter(skill =>
                    defaultSelectedSkillsValues.includes(skill.value) ||
                    (isMotorcycle && skill.value === selectedRole.delivery_box.toString())
                );

                setSelectedSkills(defaultSelectedSkills);
                setExtraSkills(options.filter(skill =>
                    !defaultSelectedSkillsValues.includes(skill.value)
                ));
            }
        }
    };

    useEffect(() => {
        setOptionsAndDefaultValues(servicesStructure);
    }, [selectedRole, editingID]);

    const handleUpdateFields = (roleLabel: any, isActive: boolean) => {
        setRoleFields((prevRoleFields: any) => ({
            ...prevRoleFields,
            [roleLabel]: isActive,
        }));
    };

    useEffect(() => {
    if (roleFields) {
            update(roleFields);
        }
    }, [roleFields, update]);

    useEffect(() => {
        if (fields) {
            setRoleFields(fields);
        }
    }, [fields]);

    const handleSelectWrapper = (selectedOptions: any) => {
        if (isVan) {
            // special behaviour for van types
            Object.values(vanSizes).forEach(vanSize => {
                const isSelected = selectedOptions.value === vanSize.value;
                update((prevFields: any) => ({
                    ...prevFields,
                    [vanSize.value]: isSelected,
                }));
            });

            setSelectedSkills([selectedOptions]);
            setExtraSkills(Object.values(vanSizes).
                filter(vanSize => vanSize.value !== selectedOptions.value));
        } else if (isMotorcycle) {
            // special behaviour for motorcycle delivery boxes
            Object.values(deliveryBoxSize).forEach(boxSize => {
                const isSelected = +selectedOptions.value === boxSize.value;
                if (isSelected) {
                    update((prevFields: any) => ({
                        ...prevFields,
                        delivery_box: boxSize.value,
                    }));
                }
            });

            setSelectedSkills([selectedOptions]);
            setExtraSkills(Object.values(deliveryBoxSize).
                filter(boxSize => boxSize.value !== selectedOptions.value));
        } else {
            const lastSelectedOption = selectedOptions.find((option: { value: any; }) =>
                !selectedSkills.some(selectedOption => selectedOption.value === option.value)
            );
            if (lastSelectedOption) {
                handleUpdateFields(lastSelectedOption.value, true);
                setSelectedSkills(selectedOptions || []);
                setExtraSkills(prevExtraSkills =>
                    prevExtraSkills.filter(skill => skill.value !== lastSelectedOption.value)
                );
            }
        }
    };

    const removeSkill = (valueToRemove: string) => {
        const selectedOption = selectedSkills.find(skill => skill.value === valueToRemove);
        handleUpdateFields(selectedOption.value, false);
        setSelectedSkills(selectedSkills.filter(option => option.value !== valueToRemove));
        setExtraSkills(prevSelectedSkills => [...prevSelectedSkills, selectedOption]);
    };

    const arrowRender = () => (
        <div className='arrow collapsed'>
            <span className="chevron"/>
        </div>
    );

    return (
        <div className='freelancer-details-select-extra-skills-container'>
            <form>
                <div className='freelancer-details-extra-skills-select'>
                    <Async
                        multi={!(isVan || isMotorcycle)}
                        className="swiper-no-swiping"
                        disabled={extraSkills.length > 0 ? disabled : true}
                        onChange={handleSelectWrapper}
                        options={extraSkills}
                        placeholder={extraSkills.length > 0 ? 'Add...' : 'No extra skills available'}
                        simpleValue={false}
                        value={((isVan || isMotorcycle) && selectedSkills) ? selectedSkills[0] : selectedSkills}
                        arrowRenderer={arrowRender}
                    />
                </div>
                <div className='freelancer-details-extra-skills-badges'>
                    {selectedSkills.map((option, index) => (
                        <div key={`selected_skill_${option.value}`} className="extra-skill-area">
                            <div
                                /* eslint-disable-next-line react/no-array-index-key */
                                key={`selected_skill_${option.value}_${index}`}
                                className={`role-item active ${disabled ? 'is-disabled' : ''}`}
                            >
                                <div className="item-circle-wrapper" onClick={() => !disabled ? removeSkill(option.value) : null}>
                                    <div className="item-circle">
                                        <i
                                            className="icon icon-remove-circle-11 red"
                                            aria-label={`Remove skill ${option.label}`}
                                        />
                                    </div>
                                    <p className="badge">{option.label}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </form>
        </div>
    );
}
